import { Injectable, inject } from '@angular/core';
import {HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpResponse} from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {select, Store} from '@ngrx/store';
import {getToken, IAuthenticationState, LogoutSuccess} from '@app/stores';
import {catchError, switchMap, take, tap} from 'rxjs/operators';
import {NavigationEnd, Router} from '@angular/router';
import {LoaderService} from '@app/services/loader.service';
import {ToastrService} from 'ngx-toastr';
import {AuthenticationService} from '@app/services/authentication.service';
import {ReportAccessTokenService} from '@app/services/report-access-token.service';
import {SessionTimeoutService} from '@app/services/session-timeout.service';
import {AppVersionService} from '@app/services/app-version.service';

/** What the auth interceptor keeps from one request to the next. */
@Injectable({providedIn: 'root'})
export class HttpAuthInterceptState {
  private router = inject(Router);

  inflightAuthRequest: Observable<string> | null = null;
  currentRoute = '/';
  /**
   * A 429 carries the server's own sentence about how long to wait. Show that, once per
   * burst - a busy page can trip the quota with a dozen requests at once, and a dozen
   * identical toasts would bury it.
   */
  lastRateLimitToastAt = 0;

  constructor() {
    this.router.events.subscribe((val) => {
      if (val instanceof NavigationEnd) {
        if (!this.router.url.includes('/login')) {
          this.currentRoute = this.router.url;
        }
      }
    });
  }
}

/**
 * Adds the access token to API requests, refreshes the session and retries once when a request
 * comes back unauthorized, and signs out when the session cannot be refreshed.
 */
export const httpAuthInterceptor: HttpInterceptorFn = (request, next) => {
  const state = inject(HttpAuthInterceptState);
  const authentication = inject<Store<IAuthenticationState>>(Store);
  const router = inject(Router);
  const toastr = inject(ToastrService);
  const loaderService = inject(LoaderService);
  const authenticationService = inject(AuthenticationService);
  const reportAccessTokenService = inject(ReportAccessTokenService);
  const sessionTimeoutService = inject(SessionTimeoutService);
  const appVersionService = inject(AppVersionService);

  // A static file under /assets is not an API call: it needs no token, and pulling it through
  // the refresh-and-retry path below couples it to session state. That is how the translation
  // dictionary came to be queued behind a failing /auth/refresh and never loaded at all.
  if (/^\/?assets\//.test(request.url)) {
    return next(request);
  }

  const withAccessToken = (unsigned: HttpRequest<any>, token: string): HttpRequest<any> => unsigned.clone({
    headers: unsigned.headers.set('Authorization', `Bearer ${token}`)
  });

  const isUnauthorized = (error: any): boolean =>
    error?.status === 401 || /Unauthorized/.test(error?.statusText || '');

  const notifyRateLimited = (error: any): void => {
    if (error?.status !== 429) {
      return;
    }
    const now = Date.now();
    if (now - state.lastRateLimitToastAt < 5000) {
      return;
    }
    state.lastRateLimitToastAt = now;
    const retryAfter = Number(error?.error?.retry_after_seconds);
    const message = error?.error?.message
      || (Number.isFinite(retryAfter) && retryAfter > 0
        ? `Too many requests. Try again in ${retryAfter} second(s).`
        : 'Too many requests. Please wait a moment and try again.');
    toastr.warning(message);
  };

  /**
   * `restoring`: the refresh was a page load picking up this browser's session. Finding none
   * is what a sign-in without "Remember me" looks like once the browser has been closed, so
   * that goes to the sign-in page without a word; a session that timed out still says why.
   */
  const handleRefreshFailure = (error: any, restoring = false): Observable<never> => {
    const code = error?.error?.code;
    const timedOut = ['AUTH_SESSION_IDLE_TIMEOUT', 'AUTH_SESSION_ABSOLUTE_TIMEOUT'].includes(code);
    if (!router.url.includes('/login') && (!restoring || timedOut)) {
      const message = code === 'AUTH_SESSION_IDLE_TIMEOUT'
        ? 'You were signed out after a period of inactivity. Please sign in again.'
        : code === 'AUTH_SESSION_ABSOLUTE_TIMEOUT'
          ? 'Your session reached its maximum lifetime. Please sign in again.'
          : 'Your session has expired. Please sign in again.';
      toastr.error(message);
    }
    loaderService.unsubscribe();
    authenticationService.setAccessToken(null);
    authentication.dispatch(LogoutSuccess());
    reportAccessTokenService.clear();
    sessionTimeoutService?.clear();
    localStorage.removeItem('currentUser');
    router.navigate(['/login'], {queryParams: {returnUrl: state.currentRoute}});
    return throwError(() => error);
  };

  const refreshAndRetry = (
    unsigned: HttpRequest<any>,
    handler: HttpHandlerFn,
    originalError: any = null,
    restoring = false
  ): Observable<HttpEvent<any>> => authenticationService.refreshSession().pipe(
    catchError((error) => handleRefreshFailure(originalError || error, restoring)),
    switchMap((response: any) => {
      if (!authenticationService.applySessionResponse(response)) {
        return handleRefreshFailure(originalError || response, restoring);
      }
      return handler(withAccessToken(unsigned, response.token));
    })
  );

  let hasSkipOAuth: boolean = request.headers.has('Skip-auth');
  const useTempToken: boolean = request.headers.has('Use-Temp-Token');
  const skipAuthRefresh: boolean = request.headers.has('Skip-auth-refresh');
  let newHeaders = request.headers
    .delete('Skip-auth')
    .delete('Use-Temp-Token')
    .delete('Skip-auth-refresh');
  const hasAccept: boolean = request.headers.has('Accept');
  if (!hasAccept) {
    newHeaders = newHeaders.append('Accept', 'application/json');
  }
  const hasContentType: boolean = request.headers.has('Content-Type');
  // A FormData body must keep the multipart Content-Type the browser generates, because only it
  // carries the boundary parameter. Forcing application/json here leaves the server unable to
  // parse the upload at all (multer sees no parts and the request 500s), so let the browser set
  // the header for these requests.
  const isFormData: boolean = request.body instanceof FormData;
  if (!hasContentType && !isFormData) {
    newHeaders = newHeaders.append('Content-Type', 'application/json; charset=utf-8');
  }
  let basicAuthRequest = request.clone({headers: newHeaders});
  const reportAccessToken = reportAccessTokenService.get();
  if (useTempToken && reportAccessToken) {
    hasSkipOAuth = true;
    basicAuthRequest = request.clone(basicAuthRequest.clone({
      headers: basicAuthRequest.headers.append('report-pass', `Bearer ${reportAccessToken}`)
    }));
    const project_id = basicAuthRequest.params.get('project_id');

    if (project_id) {
      // Add project id into headers and remove from params
      basicAuthRequest = request.clone(basicAuthRequest.clone({
        headers: basicAuthRequest.headers.append('report-project-id', project_id)
      }));
      basicAuthRequest = basicAuthRequest.clone({
        params: basicAuthRequest.params.delete('project_id'),
      });
    }
  }
  if (!state.inflightAuthRequest) {
    state.inflightAuthRequest = authentication.pipe(select(getToken));
  }
  return state.inflightAuthRequest.pipe(
    take(1),
    switchMap((newToken: string) => {
      state.inflightAuthRequest = null;
      const token = authenticationService.getAccessToken() || newToken || '';
      if (hasSkipOAuth) {
        return next(basicAuthRequest).pipe(
          catchError((error) => {
            if (useTempToken && isUnauthorized(error)) {
              reportAccessTokenService.clear();
            }
            return throwError(() => error);
          })
        );
      }
      if (!token) {
        if (localStorage.getItem('currentUser')) {
          // No token held yet: a page load picking up the session this browser had.
          return refreshAndRetry(basicAuthRequest, next, null, true);
        }
        return next(basicAuthRequest);
      }
      return next(withAccessToken(basicAuthRequest, token)).pipe(
        catchError((error) => {
          if (!isUnauthorized(error)) {
            notifyRateLimited(error);
            return throwError(() => error);
          }
          if (useTempToken) {
            reportAccessTokenService.clear();
            return throwError(() => error);
          }
          if (skipAuthRefresh) {
            return throwError(() => error);
          }
          const latestToken = authenticationService.getAccessToken();
          if (latestToken && latestToken !== token) {
            return next(withAccessToken(basicAuthRequest, latestToken));
          }
          return refreshAndRetry(basicAuthRequest, next, error);
        })
      );
    }),
    tap({
      next: (event) => {
        if (event instanceof HttpResponse) {
          sessionTimeoutService?.updateFromResponse(event);
          appVersionService?.observe(event, request.url);
        }
      },
      error: (error) => {
        if (error instanceof HttpErrorResponse) {
          sessionTimeoutService?.updateFromResponse(error);
          appVersionService?.observe(error, request.url);
        }
      }
    })
  );
};
